import {
    CurrentUser,
    CurrentUserByLink,
    UpdateCurrentUser,
    UpdateCurrentUserByLink,
    type NotificationPreferencesLink,
} from './customer-api/currentuser';
import * as models from './customer-api/models';
import * as errors from './errors';
import { NotificationPreferencesLinkFromPage } from './notification-preferences-link';
import type { LeavingPeer } from './email-preferences-page';
import { SerialQueue } from './serial-queue';

export interface SmsPreferencesPanel extends LeavingPeer {
    // The account page calls it after the customer edits their phone number.
    setPhone: (phone: string) => void;
}

type smsCommand = { kind: 'master'; enabled: boolean } | { kind: 'category'; name: string; enabled: boolean };

interface saveFeedback {
    $saved: HTMLElement;
    $saving: HTMLElement;
    $failed: HTMLElement;
    savedTimer: number;
}

// The one record of in-flight work: render() reads it to decide every
// control's disabled / busy / saving chip, so nothing else writes those.
interface pendingSave {
    command: smsCommand;
    feedback: saveFeedback;
    status: 'queued' | 'saving';
}

interface categoryRow {
    $row: HTMLElement;
    $switch: HTMLInputElement;
    feedback: saveFeedback;
}

function applyCommand(state: models.NotificationChannelPreferences, command: smsCommand): models.NotificationChannelPreferences {
    const next = new models.NotificationChannelPreferences(state);
    next.categories = state.categories.map(category => new models.NotificationCategoryPreference(category));
    if (command.kind === 'master') {
        next.enabled = command.enabled;
        return next;
    }
    for (const category of next.categories) {
        if (category.name === command.name) {
            category.enabled = command.enabled;
        }
    }
    return next;
}

function commandUpdate(command: smsCommand): models.CustomerUserUpdate {
    if (command.kind === 'master') {
        let state: models.TextMessagesStateValue;
        if (command.enabled) {
            state = models.TextMessagesStateValue.TEXT_MESSAGES_STATE_ON;
        } else {
            state = models.TextMessagesStateValue.TEXT_MESSAGES_STATE_OFF;
        }
        return new models.CustomerUserUpdate({
            notificationPreferences: {
                textMessages: { state },
            },
        });
    }
    return new models.CustomerUserUpdate({
        notificationPreferences: {
            textMessages: { categories: { [command.name]: command.enabled } },
        },
    });
}

export function InitSmsPreferencesPanel(opts: {
    mode: 'link' | 'session';
    phone?: string;
    onChangeNumber?: () => void;
    categoryDescriptions?: Record<string, string>;
    // Link mode: the page shares one signed link between its channels, so a
    // 403 on this channel's save is the page's broken link, not this panel's.
    onInvalidToken?: () => void;
}): SmsPreferencesPanel {
    const $root = document.querySelector('#sprefs') as HTMLElement;
    const $loading = document.querySelector('#sprefs-loading') as HTMLElement;
    const $loadFailed = document.querySelector('#sprefs-load-failed') as HTMLElement;
    const $retry = document.querySelector('#sprefs-retry') as HTMLButtonElement;
    const $damaged = document.querySelector('#sprefs-damaged') as HTMLElement | null;
    const $legacy = document.querySelector('#sprefs-legacy') as HTMLElement | null;
    const $hasPhone = document.querySelector('#sprefs-has-phone') as HTMLElement | null;
    const $noPhone = document.querySelector('#sprefs-no-phone') as HTMLElement | null;
    const $phone = document.querySelector('#sprefs-phone') as HTMLElement | null;
    const $changeNumber = document.querySelector('#sprefs-change-number') as HTMLElement | null;
    const $addNumber = document.querySelector('#sprefs-add-number') as HTMLElement | null;
    const $master = document.querySelector('#sprefs-master') as HTMLInputElement;
    const $offNote = document.querySelector('#sprefs-off-note') as HTMLElement;
    const $categoriesBlock = document.querySelector('#sprefs-categories-block') as HTMLElement;
    const $categories = document.querySelector('#sprefs-categories') as HTMLElement;
    const $template = document.querySelector('#sprefs-category-template') as HTMLTemplateElement;
    const masterFeedback: saveFeedback = {
        $saved: document.querySelector('#sprefs-master-saved') as HTMLElement,
        $saving: document.querySelector('#sprefs-master-saving') as HTMLElement,
        $failed: document.querySelector('#sprefs-master-failed') as HTMLElement,
        savedTimer: 0,
    };

    type panelState = 'loading' | 'ready' | 'load-failed' | 'invalid-token' | 'legacy';
    function setState(state: panelState) {
        $loading.classList.toggle('d-none', state !== 'loading');
        $root.classList.toggle('d-none', state !== 'ready');
        $loadFailed.classList.toggle('d-none', state !== 'load-failed');
        $damaged?.classList.toggle('d-none', state !== 'invalid-token');
        $legacy?.classList.toggle('d-none', state !== 'legacy');
        if (state === 'invalid-token') {
            opts.onInvalidToken?.();
        }
    }
    function isInvalidToken(err: unknown): boolean {
        return opts.mode === 'link' && errors.IsAxiosError(err) && err.response?.status === 403;
    }

    let link: NotificationPreferencesLink | null = null;
    if (opts.mode === 'link') {
        const fromPage = NotificationPreferencesLinkFromPage();
        const inert: SmsPreferencesPanel = { reload: () => Promise.resolve(), setPhone: () => undefined, freeze: () => Promise.resolve(), thaw: () => undefined };
        if (fromPage.kind === 'legacy') {
            setState('legacy');
            return inert;
        }
        if (fromPage.kind === 'invalid') {
            setState('invalid-token');
            return inert;
        }
        link = fromPage.link;
    }

    const rows = new Map<string, categoryRow>();
    let authoritative: models.NotificationChannelPreferences | null = null;
    let frozen = false;
    const pending: pendingSave[] = [];

    function fold(base: models.NotificationChannelPreferences, commands: smsCommand[]): models.NotificationChannelPreferences {
        let state = base;
        for (const command of commands) {
            state = applyCommand(state, command);
        }
        return state;
    }

    // Loads and saves share one chain so a reload cannot paint over an in-flight save.
    const queue = new SerialQueue();
    function runQueued<T>(task: () => Promise<T>): Promise<T> {
        return queue.run(task);
    }

    // Frozen: nothing here may reach the server until thaw() (see LeavingPeer).
    function freeze(): Promise<void> {
        frozen = true;
        $root.setAttribute('aria-busy', 'true');
        $retry.disabled = true;
        render();
        // The chain already swallows failures, so this settles once every
        // queued save and load has finished either way.
        return queue.idle();
    }
    function thaw() {
        frozen = false;
        $root.removeAttribute('aria-busy');
        $retry.disabled = false;
        render();
    }

    function pendingForMaster(): pendingSave | undefined {
        return pending.find(entry => entry.command.kind === 'master');
    }

    function pendingForCategory(name: string): pendingSave | undefined {
        return pending.find(entry => entry.command.kind === 'category' && entry.command.name === name);
    }

    function setControlBusy($control: HTMLInputElement, busy: boolean) {
        if (busy) {
            $control.setAttribute('aria-busy', 'true');
        } else {
            $control.removeAttribute('aria-busy');
        }
    }

    // A row's save state is published once, on the status region holding its
    // chips, as data-sprefs-state (queued | saving): the theme shows the
    // matching chip from that alone, never by reconstructing it from the
    // switch's disabled/aria-busy pair (a switch is also disabled while the
    // master is off or mid-save, and neither of those is "queued").
    function setPending(feedback: saveFeedback, status: pendingSave['status'] | undefined) {
        const $region = feedback.$saving.parentElement;
        if (status) {
            $region?.setAttribute('data-sprefs-state', status);
        } else {
            $region?.removeAttribute('data-sprefs-state');
        }
        if (status === 'saving') {
            feedback.$saving.classList.remove('d-none');
        } else {
            feedback.$saving.classList.add('d-none');
        }
    }

    function render() {
        if (!authoritative) {
            return;
        }
        const folded = fold(authoritative, pending.map(entry => entry.command));
        $master.checked = folded.enabled;
        const masterEntry = pendingForMaster();
        $master.disabled = frozen || masterEntry !== undefined;
        setControlBusy($master, masterEntry?.status === 'saving');
        setPending(masterFeedback, masterEntry?.status);
        if (!folded.enabled && rows.size > 0) {
            $offNote.classList.remove('d-none');
        } else {
            $offNote.classList.add('d-none');
        }
        for (const [name, row] of rows) {
            const categoryEntry = pendingForCategory(name);
            const category = folded.categories.find(item => item.name === name);
            row.$switch.checked = category?.enabled === true;
            if (!folded.enabled) {
                row.$row.classList.add('eprefs-muted');
            } else {
                row.$row.classList.remove('eprefs-muted');
            }
            // Also inert while a master save is pending: the fold already shows
            // the master's new position, but until the server confirms it the
            // categories' editability would be riding on a save that can fail.
            row.$switch.disabled = frozen || !folded.enabled || categoryEntry !== undefined || masterEntry !== undefined;
            setControlBusy(row.$switch, categoryEntry?.status === 'saving');
            setPending(row.feedback, categoryEntry?.status);
        }
    }

    function bindRows(categories: models.NotificationCategoryPreference[]) {
        // Rebuilt from every successful load: a reload may bring a different
        // category set, and stale rows would keep folding retired names into
        // the PATCH. By this task's turn in the queue every earlier save has
        // settled, so no pending command references a dropped row.
        rows.clear();
        $categories.replaceChildren();
        if (categories.length === 0) {
            $categoriesBlock.classList.add('d-none');
        } else {
            $categoriesBlock.classList.remove('d-none');
        }
        for (const category of categories) {
            const $frag = $template.content.cloneNode(true) as DocumentFragment;
            const $row = $frag.firstElementChild as HTMLElement;
            const $name = $row.querySelector('.sprefs-category-name') as HTMLElement;
            const $desc = $row.querySelector('.sprefs-category-desc') as HTMLElement;
            const $switch = $row.querySelector('.sprefs-category-switch') as HTMLInputElement;
            const feedback: saveFeedback = {
                $saved: $row.querySelector('.sprefs-category-saved') as HTMLElement,
                $saving: $row.querySelector('.sprefs-category-saving') as HTMLElement,
                $failed: $row.querySelector('.sprefs-category-failed') as HTMLElement,
                savedTimer: 0,
            };
            $name.textContent = category.name;
            $desc.textContent = opts.categoryDescriptions?.[category.name] ?? '';
            $switch.setAttribute('aria-label', category.name);
            $switch.id = 'sprefs-category-' + category.name.replace(/[^A-Za-z0-9]+/g, '-');
            // A Bootstrap 4 custom switch is its label: without the `for` the
            // hidden native input never gets the click.
            $row.querySelectorAll('label').forEach($label => {
                $label.htmlFor = $switch.id;
            });
            const row: categoryRow = { $row, $switch, feedback };
            rows.set(category.name, row);
            $switch.addEventListener('change', () => {
                submit({ kind: 'category', name: category.name, enabled: $switch.checked }, feedback);
            });
            $categories.appendChild($row);
        }
    }

    function showSaved(feedback: saveFeedback) {
        if (feedback.savedTimer !== 0) {
            window.clearTimeout(feedback.savedTimer);
        }
        feedback.$saved.classList.remove('d-none');
        feedback.savedTimer = window.setTimeout(() => {
            feedback.$saved.classList.add('d-none');
            feedback.savedTimer = 0;
        }, 2500);
    }

    function submit(command: smsCommand, feedback: saveFeedback): void {
        if (frozen) {
            // The click already moved the switch; put it back where the
            // server has it.
            render();
            return;
        }
        if (feedback.savedTimer !== 0) {
            window.clearTimeout(feedback.savedTimer);
            feedback.savedTimer = 0;
        }
        feedback.$saved.classList.add('d-none');
        feedback.$failed.classList.add('d-none');
        const entry: pendingSave = { command, feedback, status: 'queued' };
        pending.push(entry);
        render();
        void runQueued(async () => {
            entry.status = 'saving';
            render();
            try {
                const update = commandUpdate(command);
                let resp: models.CurrentUserResponse;
                if (opts.mode === 'link' && link) {
                    resp = await UpdateCurrentUserByLink(link, update);
                } else {
                    resp = await UpdateCurrentUser(update);
                }
                authoritative = resp.notificationPreferences.textMessages;
                showSaved(feedback);
            } catch (err) {
                if (isInvalidToken(err)) {
                    setState('invalid-token');
                    return;
                }
                errors.ShowError(err);
                feedback.$failed.classList.remove('d-none');
            } finally {
                pending.splice(pending.indexOf(entry), 1);
                render();
            }
        });
    }

    function load(): Promise<void> {
        if (frozen) {
            return Promise.resolve();
        }
        return runQueued(async () => {
            setState('loading');
            try {
                let resp: models.CurrentUserResponse;
                if (opts.mode === 'link' && link) {
                    resp = await CurrentUserByLink(link);
                } else {
                    resp = await CurrentUser();
                }
                authoritative = resp.notificationPreferences.textMessages;
                bindRows(authoritative.categories);
                render();
                setState('ready');
            } catch (err) {
                if (isInvalidToken(err)) {
                    setState('invalid-token');
                    return;
                }
                errors.ShowError(err);
                setState('load-failed');
            }
        });
    }

    let phoneSeq = 0;
    function setPhone(phone: string) {
        // Published on the panel so a theme can mute what cannot deliver yet:
        // the switches stay usable, since the choice is kept and goes live the
        // moment a number is added.
        $root.classList.toggle('sprefs-no-number', phone.trim() === '');
        if (!$hasPhone || !$noPhone || !$phone) {
            return;
        }
        phoneSeq += 1;
        const seq = phoneSeq;
        if (phone.trim() === '') {
            $hasPhone.classList.add('d-none');
            $noPhone.classList.remove('d-none');
            return;
        }
        $noPhone.classList.add('d-none');
        $hasPhone.classList.remove('d-none');
        // Raw first so the line is never blank while intl-tel-input loads.
        $phone.textContent = phone;
        void import('./phone').then(mod => {
            if (seq !== phoneSeq) {
                return;
            }
            const formatted = mod.Human(phone);
            if (formatted !== '') {
                $phone.textContent = formatted;
            }
        }).catch(() => undefined);
    }

    $master.addEventListener('change', () => {
        submit({ kind: 'master', enabled: $master.checked }, masterFeedback);
    });
    if ($changeNumber && opts.onChangeNumber) {
        $changeNumber.addEventListener('click', e => {
            e.preventDefault();
            opts.onChangeNumber?.();
        });
    }
    if ($addNumber && opts.onChangeNumber) {
        $addNumber.addEventListener('click', e => {
            e.preventDefault();
            opts.onChangeNumber?.();
        });
    }
    $retry.addEventListener('click', () => {
        void load();
    });

    if (opts.phone !== undefined) {
        setPhone(opts.phone);
    }
    void load();

    return { reload: load, setPhone, freeze, thaw };
}
